import { CollapseButton, DataGrid } from "@msys/ui";
import type { StateStore } from "@msys/ui";
import { Box } from "@mui/material";
import type {
  DataGridPremiumProps,
  GridCellEditStopParams,
  GridCellParams,
  GridColDef,
  GridGroupingColDefOverride,
  GridGroupNode,
  GridInitialState,
  GridRowId,
  GridRowOrderChangeParams,
  GridRowSelectionModel,
  GridSortCellParams,
  IsRowReorderableParams,
} from "@mui/x-data-grid-premium";
import {
  GridCellEditStopReasons,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { Agreement, ItemType } from "../../../clients/graphqlTypes.js";
import { size } from "../../../common/MuiThemeProvider.js";
import { NoPropagateBox } from "../../commons/NoPropagateBox.js";
import { useMoveTreeItem } from "../../trees-virtual/hooks/useMoveTreeItem.js";
import {
  createRecursiveTryFocusElementFunction,
  isInTestMode,
} from "../../utils.js";
import type { DocDataGrid_ItemFragment } from "./DocDataGrid.generated.js";
import { DocDataGridSavingContext } from "./DocDataGridSavingContext.js";
import { canDropDocDataGridRow, DropPosition } from "./helpers.js";
import {
  DOC_DATA_GRID_ROW_SX,
  getDocDataGridItemRowClassName,
} from "./styles.js";
import type { DocDataGridRow } from "./types.js";

// ---------------------------------------------------------------------------
// Render prop types
// ---------------------------------------------------------------------------

export interface DocDataGridItemComponentProps<
  T extends DocDataGrid_ItemFragment,
> {
  item: T;
  row: DocDataGridRow<T>;
  parentItem: T | null;
}

export type DocDataGridItemComponent<T extends DocDataGrid_ItemFragment> =
  React.ComponentType<DocDataGridItemComponentProps<T>>;

export interface DocDataGridInputComponentProps<
  T extends DocDataGrid_ItemFragment,
> {
  id?: string;
  onBeforeItemCreate?: () => Promise<void> | void;
  onAfterItemCreated?: () => Promise<void> | void;

  /** Parent item for this input row (same contract as InputComponentProps.item in trees) */
  parentItem: T;
  isRootItem: boolean;
  /** Direct item-kind children of the parent */
  itemChildren: T[];
  nodeRank: number;
}

export type DocDataGridInputComponent<T extends DocDataGrid_ItemFragment> =
  React.ComponentType<DocDataGridInputComponentProps<T>>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props<T extends DocDataGrid_ItemFragment> {
  rows: DocDataGridRow<T>[];

  projectId: string | null;
  docId: string | null;

  /** Controlled expansion — IDs of currently expanded groups */
  isItemExpanded: (itemId: string) => boolean;
  expandedRowIds: string[] | undefined;
  onExpandedRowIdChange: (id: string, expanded: boolean) => void;

  /** Whether to show expand buttons (can hide when no relevant children) */
  showExpandButton?: (item: T) => boolean;

  /** Whether to render the root item as the top-level row. Defaults to true. */
  showRootItem?: boolean;

  /** ID of the currently selected item — highlights the corresponding row */
  selectedItemId?: string | null;

  enableDragging: boolean;
  documentItemTypes: ItemType[];

  /** Pixels of indentation per depth level. Defaults to 24. */
  indentWidth?: number;

  ItemComponent: DocDataGridItemComponent<T>;
  InputComponent: DocDataGridInputComponent<T>;

  /** Props for calculating row classes */
  docAgreement: Agreement | undefined;
  isItemMuted?: (item: T) => boolean;

  /** Optional column definitions — passed directly to the MUI DataGrid */
  columns?: GridColDef<DocDataGridRow<T>>[];

  /** Optional row-update handler for inline cell editing */
  processRowUpdate?: (
    newRow: DocDataGridRow<T>,
    oldRow: DocDataGridRow<T>
  ) => Promise<DocDataGridRow<T>>;

  /** Persisted DataGrid state (column visibility, sorting, etc.) */
  stateStore?: StateStore;

  /** Default DataGrid state applied on first render or when stateStore has no saved value */
  initialState?: GridInitialState;

  /** Whether a row is currently being saved — provided to SavingIndicatorCell via context */
  isSavingRow?: (rowId: string) => boolean;

  /** Enable native MUI DataGrid row selection with checkboxes */
  enableMultiSelect?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
}

// ---------------------------------------------------------------------------
// sortComparator — rank-based ordering within each group
// ---------------------------------------------------------------------------

const rankComparator = <T extends DocDataGrid_ItemFragment>(
  _v1: DocDataGridRow<T>,
  _v2: DocDataGridRow<T>,
  cellParams1: GridSortCellParams<DocDataGridRow<T>>,
  cellParams2: GridSortCellParams<DocDataGridRow<T>>
) => _v1.rank - _v2.rank;

// ---------------------------------------------------------------------------
// columns — default columns definition to always keep the same variable
// ---------------------------------------------------------------------------

const defaultColumns: GridColDef<DocDataGridRow<any>>[] = [];

// Suppress the indeterminate visual on parent rows in multi-select (misleading
// with parents=false). This slotProps value does flow to every baseCheckbox
// consumer, including the columns-management panel, but the only panel checkbox
// that uses the indeterminate state — the "Show/Hide all" toggle — is disabled
// by the @msys/ui DataGrid wrapper (disableShowHideToggle: true), and the
// per-column visibility checkboxes never go indeterminate, so the panel is
// visually unaffected.
const nonIndeterminateSlotProps = {
  baseCheckbox: { indeterminate: false },
} as NonNullable<DataGridPremiumProps["slotProps"]>;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DocDataGrid = <T extends DocDataGrid_ItemFragment>({
  rows,
  projectId,
  docId,
  isItemExpanded,
  expandedRowIds,
  onExpandedRowIdChange,
  showExpandButton,
  showRootItem = true,
  selectedItemId,
  indentWidth = 20,
  enableDragging,
  documentItemTypes,
  ItemComponent,
  InputComponent,
  docAgreement,
  isItemMuted,
  columns,
  processRowUpdate,
  stateStore,
  initialState,
  isSavingRow,
  enableMultiSelect,
  rowSelectionModel,
  onRowSelectionModelChange,
}: Props<T>) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);

  const apiRef = useGridApiRef();

  const [moveTreeItem, isMoveLoading] = useMoveTreeItem({ projectId, docId });

  // Stores drop context between isValidRowReorder → onRowOrderChange
  const pendingDropRef = React.useRef<{
    sourceRowId: string;
    targetRowId: string;
    dropPosition: DropPosition;
  } | null>(null);

  // ---------------------------------------------------------------------------
  // Focus reset
  // ---------------------------------------------------------------------------

  const inputRestoreSelectorRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (inputRestoreSelectorRef.current) {
      const selector = inputRestoreSelectorRef.current;
      inputRestoreSelectorRef.current = null;
      if (isInTestMode()) return;
      setTimeout(() => {
        createRecursiveTryFocusElementFunction(selector)();
      });
    }
  }, [rows]);

  // ---------------------------------------------------------------------------
  // Expansion
  // ---------------------------------------------------------------------------

  // Subscribe to expansion events and propagate to caller
  React.useEffect(() => {
    return apiRef.current?.subscribeEvent(
      "rowExpansionChange",
      (params: { id: string | number; childrenExpanded?: boolean }) => {
        onExpandedRowIdChange(
          String(params.id),
          params.childrenExpanded ?? false
        );
      }
    );
  }, [apiRef, expandedRowIds, onExpandedRowIdChange, isItemExpanded]);

  // Sync external expandedRowIds into DataGrid imperatively (e.g. search auto-expand)
  React.useEffect(() => {
    const api = apiRef.current;
    if (!api) return;
    rows.forEach(row => {
      if (row.kind !== "item") return;
      try {
        const node = api.getRowNode(row.id);
        if (node && "childrenExpanded" in node) {
          const shouldBeExpanded =
            !expandedRowIds ||
            row.item?.isRootItem ||
            expandedRowIds.includes(row.id);
          if (node.childrenExpanded !== shouldBeExpanded) {
            api.setRowChildrenExpansion(row.id, shouldBeExpanded);
          }
        }
      } catch {
        // row might not be mounted yet
      }
    });
  }, [apiRef, expandedRowIds, rows]);

  // ---------------------------------------------------------------------------
  // Drag-and-drop
  // ---------------------------------------------------------------------------

  const isRowReorderable = React.useCallback(
    <T extends DocDataGrid_ItemFragment>(
      params: IsRowReorderableParams<DocDataGridRow<T>>
    ) =>
      params.row.kind !== "input" &&
      !(showRootItem && params.row.item?.isRootItem),
    [showRootItem]
  );

  const isValidRowReorder = React.useCallback(
    (context: {
      sourceNode: { id: GridRowId };
      targetNode: { id: GridRowId; parent: GridRowId | null };
      prevNode: { id: GridRowId } | null;
      nextNode: { id: GridRowId } | null;
      dropPosition: DropPosition;
    }) => {
      const valid = canDropDocDataGridRow({
        sourceRowId: String(context.sourceNode.id),
        targetRowId: String(context.targetNode.id),
        dropPosition: context.dropPosition,
        prevRowId: context.prevNode ? String(context.prevNode.id) : null,
        nextRowId: context.nextNode ? String(context.nextNode.id) : null,
        rows,
        documentItemTypes,
        isLoading: isMoveLoading,
      });
      if (valid) {
        pendingDropRef.current = {
          sourceRowId: String(context.sourceNode.id),
          targetRowId: String(context.targetNode.id),
          dropPosition: context.dropPosition,
        };
      }
      return valid;
    },
    [rows, documentItemTypes, isMoveLoading]
  );

  const setTreeDataPath = React.useCallback(
    (newPath: string[], row: DocDataGridRow<T>): DocDataGridRow<T> => ({
      ...row,
      path: newPath,
      parentId: newPath.length > 1 ? newPath[newPath.length - 2] : row.parentId,
    }),
    []
  );

  const onRowOrderChange = React.useCallback(
    async (_params: GridRowOrderChangeParams) => {
      const pending = pendingDropRef.current;
      if (!pending) return;
      pendingDropRef.current = null;

      const { sourceRowId, targetRowId, dropPosition } = pending;
      const targetRow = rows.find(r => r.id === targetRowId);
      if (!targetRow) return;

      let newParentId: string;
      let newRank: number;

      if (dropPosition === "inside") {
        newParentId = targetRowId;
        const siblings = rows.filter(
          r => r.parentId === targetRowId && r.kind === "item"
        );
        const lastSibling =
          siblings.length > 0
            ? siblings.sort((a, b) => a.rank - b.rank).at(-1)
            : null;
        newRank = (lastSibling?.rank ?? 0) + 1;
      } else {
        newParentId = targetRow.parentId ?? "";
        newRank =
          dropPosition === "above" ? targetRow.rank : targetRow.rank + 1;
      }

      if (newParentId) {
        await moveTreeItem(sourceRowId, newParentId, newRank);
      }
    },
    [rows, moveTreeItem]
  );

  // ---------------------------------------------------------------------------
  // Tree data
  // ---------------------------------------------------------------------------

  const getRowId = React.useCallback((row: DocDataGridRow<T>) => row.id, []);

  const getTreeDataPath = React.useCallback(
    (row: DocDataGridRow<T>) => row.path,
    []
  );

  const isGroupExpandedByDefault = React.useCallback(
    (node: GridGroupNode) => {
      const row = apiRef.current?.getRow?.(
        String(node.id) ?? null
      ) as DocDataGridRow<T> | null;
      if (row?.kind === "input") return false;
      if (row?.item?.isRootItem) return true;
      return (
        !row?.parentId ||
        !expandedRowIds ||
        expandedRowIds.includes(String(node.id))
      );
    },
    [apiRef, expandedRowIds]
  );

  const groupingColDef: GridGroupingColDefOverride<DocDataGridRow<T>> =
    React.useMemo(
      () => ({
        headerName: t("Title", { ns: "QuoteItem" }),
        sortComparator: rankComparator,
        renderCell: params => {
          const row = params.row;
          const depth = row.path.length - 1;
          if (row.kind === "item" && row.item) {
            const shouldShow =
              !row.item.isRootItem &&
              (showExpandButton?.(row.item) ?? row.item.hasChildren);
            const isExpanded = isItemExpanded(row.id);
            return (
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                sx={{ pl: `${(depth - 1) * indentWidth}px` }}
              >
                {shouldShow ? (
                  <CollapseButton
                    isExpanded={isExpanded}
                    setIsExpanded={expanded =>
                      onExpandedRowIdChange(row.id, expanded)
                    }
                    style={{ marginRight: "4px" }}
                  />
                ) : depth > 0 ? (
                  <Box sx={{ width: "32px", flexShrink: 0, mr: 0.5 }} />
                ) : null}
                <Box flex={1} minWidth={0}>
                  <ItemComponent
                    item={row.item}
                    row={row}
                    parentItem={row.parentItem}
                  />
                </Box>
              </Box>
            );
          }
          if (row.kind === "input" && row.item) {
            const item = row.item;
            return (
              <NoPropagateBox
                flex={1}
                display="flex"
                alignItems="center"
                sx={{ pl: `${(depth - 1) * indentWidth}px` }}
              >
                <Box sx={{ width: "32px", flexShrink: 0, mr: 0.5 }} />
                <InputComponent
                  id={`msys-datagrid-input-${item.type}-${item.id}`}
                  parentItem={item}
                  isRootItem={item.isRootItem}
                  itemChildren={row.itemChildren}
                  nodeRank={row.rank}
                  onBeforeItemCreate={() => {
                    // the positioned (add above/below) input is re-anchored +
                    // focused by the view after creation, so skip the
                    // bottom-focus restore for it
                    if (row.isAdditionalInput) return;
                    inputRestoreSelectorRef.current = `input#msys-datagrid-input-${item.type}-${item.id}:enabled`;
                  }}
                />
              </NoPropagateBox>
            );
          }
          return null;
        },
        flex: 1,
        minWidth: 200,
        hideable: false,
        disableColumnMenu: false,
      }),
      [
        t,
        showExpandButton,
        isItemExpanded,
        indentWidth,
        ItemComponent,
        onExpandedRowIdChange,
        InputComponent,
      ]
    );

  // ---------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------

  // Expand selected row when selectedItemId changes.
  React.useEffect(() => {
    if (!selectedItemId) return;
    const api = apiRef.current;
    if (!api) return;
    try {
      const node = api.getRowNode(selectedItemId);
      if (node && "childrenExpanded" in node && !node.childrenExpanded) {
        api.setRowChildrenExpansion(selectedItemId, true);
      }
    } catch {
      // row might not be mounted yet
    }
  }, [apiRef, selectedItemId]);

  // rowSelectionModel does not highlight group rows (rows with children) in
  // tree-data mode, so we use getRowClassName to apply Mui-selected directly.
  // Also applies caller-supplied item row classes (colored lines, backgrounds).
  const getRowClassName = React.useCallback(
    (params: { id: GridRowId }) => {
      const classes: string[] = [];
      if (params.id === selectedItemId) classes.push("dg-row--open");
      const row = rows.find(r => r.id === params.id);
      if (row?.kind === "item" && row.item) {
        const cls = getDocDataGridItemRowClassName(
          row.item,
          docAgreement,
          isItemMuted?.(row.item) ?? false
        );
        if (cls) classes.push(cls);
      }
      return classes.join(" ");
    },
    [selectedItemId, rows, docAgreement, isItemMuted]
  );

  // ---------------------------------------------------------------------------
  // Multi-select
  // ---------------------------------------------------------------------------

  const isRowSelectable = React.useCallback(
    (params: { row: DocDataGridRow<T> }) =>
      params.row.kind !== "input" && !params.row.item?.isRootItem,
    []
  );

  const rowSelectionPropagation = React.useMemo(
    () => ({ descendants: false, parents: false }),
    []
  );

  // ---------------------------------------------------------------------------
  // Single-click editing
  // ---------------------------------------------------------------------------

  // Prevent the DataGrid from stopping edit mode when focus moves into a
  // description Popover (which is portaled outside the grid's DOM subtree).
  const handleCellEditStop = React.useCallback(
    (
      _params: GridCellEditStopParams,
      event: { defaultMuiPrevented?: boolean; relatedTarget?: unknown }
    ) => {
      if (_params.reason !== GridCellEditStopReasons.cellFocusOut) return;
      const relatedTarget = event.relatedTarget as Element | null;
      if (
        relatedTarget?.closest?.("[doc-data-grid-popover]") ||
        document.activeElement?.closest("[doc-data-grid-popover]")
      ) {
        event.defaultMuiPrevented = true;
      }
    },
    []
  );

  const handleCellClick = React.useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (!params.isEditable) return;
      if (
        (event.target as any).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }
      apiRef.current?.startCellEditMode({ id: params.id, field: params.field });
    },
    [apiRef]
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const savingContextValue = React.useMemo(
    () => ({ isSavingRow: isSavingRow ?? (() => false) }),
    [isSavingRow]
  );

  return (
    <DocDataGridSavingContext.Provider value={savingContextValue}>
      <DataGrid<DocDataGridRow<T>>
        apiRef={apiRef}
        rows={rows}
        columns={columns ?? defaultColumns}
        processRowUpdate={processRowUpdate}
        stateStore={stateStore}
        initialState={initialState}
        treeData
        pagination={false}
        sortingMode="client"
        getRowId={getRowId}
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        isGroupExpandedByDefault={isGroupExpandedByDefault}
        rowReordering={enableDragging}
        isRowReorderable={isRowReorderable}
        isValidRowReorder={isValidRowReorder}
        setTreeDataPath={setTreeDataPath}
        onRowOrderChange={onRowOrderChange}
        getRowClassName={getRowClassName}
        onCellEditStop={handleCellEditStop}
        onCellClick={handleCellClick}
        disableRowSelectionOnClick
        checkboxSelection={enableMultiSelect}
        slotProps={enableMultiSelect ? nonIndeterminateSlotProps : undefined}
        isRowSelectable={enableMultiSelect ? isRowSelectable : undefined}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        rowSelectionPropagation={
          enableMultiSelect ? rowSelectionPropagation : undefined
        }
        autoHeight={false}
        borderless
        density="standard"
        hideFooter
        rowHeight={size.tableItemFullHeight}
        columnHeaderHeight={size.tableItemFullHeight}
        sx={DOC_DATA_GRID_ROW_SX}
      />
    </DocDataGridSavingContext.Provider>
  );
};
